import type { Request, Response } from 'express';
import requestIp from 'request-ip';

import { getSwitchrInstance } from '@/lib/switchr/server/getSwitchr';

// Get the type information from the client's type
type StatsigClient = ReturnType<typeof getSwitchrInstance>['getVendorClient'] extends () => infer R ? R : never;
type StatsigUser = Parameters<StatsigClient['checkGate']>[0];

export type StatsigServerProps = {
  user: ReturnType<typeof getStatsigUser>;
  data: string;
  key: string;
};

export const getIsInternal = (req: Request): boolean => {
  const isInternalHost = req.hostname !== 'submit.shutterstock.com';
  const isInternalWaf = req.headers['x-amzn-waf-sstk-internal'] === 'true';

  return isInternalHost || isInternalWaf || process.env.NODE_CONFIG_ENV !== 'production';
};

// Add your flag in COOKIE_SUPPORTED_FLAGS if you are using Statsig for bucketing your gate and you want to use sticky bucketing in your tests and manual testing
export const COOKIE_SUPPORTED_FLAGS: string[] = [];
// Add your flag in COOKIE_SUPPORTED_FLAGS_OUTSIDE_VPN if you are using Statsig to analyze your gate but not for bucketing
export const COOKIE_SUPPORTED_FLAGS_OUTSIDE_VPN: string[] = [];

/**
 * This function converts query params into the overrides which is used to force a request into the on or off value for a
 * statsig feature gate
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isInternal - Whether the request is coming from an internal source
 * @param {Request} options.req - Express request object
 * @param {Response} options.res - Express response object
 * @returns {string|undefined} Comma-separated list of gate overrides or undefined if none
 */
export const calculateGateOverrides = ({
  isInternal,
  req,
  res,
}: {
  isInternal: boolean;
  req: Request;
  res: Response;
}): string | undefined => {
  const overrides = [];

  // Support for mid-launch migrated launch darkly flags or edge flags from #2979
  const flags = req.query?.flag || res.locals.featureFlags || undefined;

  if (isInternal && flags) {
    Object.entries(flags).forEach(([key, value]) => {
      if (value === 'false' || value === false) {
        overrides.push(`${key}-off`);
      } else if (value === 'true' || value === true) {
        overrides.push(`${key}-on`);
      }
    });
  }

  // Support for sticky feature gate testing
  Object.entries(req.cookies).forEach(([key, value]) => {
    if (isInternal && COOKIE_SUPPORTED_FLAGS.includes(key)) {
      overrides.push(`${key}-${value === 'true' ? 'on' : 'off'}`);
    }

    if (COOKIE_SUPPORTED_FLAGS_OUTSIDE_VPN.includes(key)) {
      overrides.push(`${key}-${value === 'true' ? 'on' : 'off'}`);
    }
  });

  // Support for studio-api feature flags x-shutterstock-features
  const gatesFromHeader = req.get('x-shutterstock-features')?.split(',') || [];

  gatesFromHeader.forEach((key) => {
    overrides.push(`${key}-on`);
  });

  // Deprecated: use flags instead of override_id
  const overrideId = isInternal && typeof req.query?.override_id === 'string' ? req.query?.override_id : undefined;

  if (overrideId) {
    overrides.push(`${overrideId}-on`);
  }

  return overrides.join(',');
};

export const getStatsigUser = ({
  req,
  res,
  router,
}: {
  req: Request;
  res: Response;
  router: { pathname: string; asPath: string };
}): StatsigUser => {
  // Don't want to allow URL or cookie overriding for non internal users to prevent any malicious usage.
  const isInternal = getIsInternal(req);
  const overrideId = isInternal && typeof req.query?.override_id === 'string' ? req.query?.override_id : undefined;
  const testE2e = isInternal && req.cookies?.switchr_e2e ? req.cookies.switchr_e2e : undefined;
  const browserLocale = typeof req.acceptsLanguages === 'function' ? req.acceptsLanguages()[0] : 'en';
  const overrides = calculateGateOverrides({ isInternal, req, res });

  const url =
    typeof req.get === 'function'
      ? new URL(`${req.protocol}://${req.get('host')}${req.originalUrl}`).href
      : req.originalUrl;
  // const fullUrl = new URL(`${req.protocol}://${req.get('host')}${req.originalUrl}`);

  const user: StatsigUser = {
    locale: browserLocale,
    country: res.locals?.region,
    userAgent: req.headers['user-agent'],
    appVersion: req.cookies?.n_v,
    ip: requestIp.getClientIp(req) || undefined,
    custom: {
      brand: 'shutterstock',
      referer: req.headers.referer,
      url,
      pageUrl: router.asPath,
      isLoggedIn: !!req.user?.id,
      pageLanguage: res.locals?.locale,
      site: 'contributor',
      pageType: router.pathname,
      // subPage: undefined, // subPage not used in contributorWeb it seems like
      // 'test-e2e' value watched in custom segment used in experiments conditional override
      // to force experiments to control for automated testing
      // https://console.statsig.com/2FkSXPTvtQJC4nZkAA10eg/segments/segment%3Atest-e2e
      ...(testE2e ? { 'test-e2e': testE2e } : {}),
      ...(overrides ? { customOverrides: overrides } : {}),
    },
    customIDs: {
      stableID: res.locals?.anonymousId,
      anonymous_id: res.locals?.anonymousId,
      accounts_id: req.user?.id ? `${req.user.id}` : '',
      ...(overrideId ? { override_id: overrideId } : {}),
    },
  };

  return user;
};

export const getStatsigData = ({ user, key }: { user: StatsigUser; key: string }) => {
  try {
    const Statsig = getSwitchrInstance().getVendorClient();

    const values = Statsig.getClientInitializeResponse(user, key, {
      hash: 'djb2', // 🔥 'djb2' is required. By default this would be 'sha256'.
      // hash: 'none', // if debugging locally set the value to none to be able to see gateIds.
    });

    const data = JSON.stringify(values);

    return data;
  } catch (err) {
    /* eslint no-console: ["error", { allow: ["warn", "error"] }] */
    console.warn(err, 'FAE-777 - getStatsigData failed');

    const fallbackData = JSON.stringify({ has_updates: false, user });

    return fallbackData;
  }
};

export function getStatsigServerProps({
  req,
  res,
  router,
}: {
  req: Request;
  res: Response;
  router: { pathname: string; asPath: string };
}) {
  // This is a public key on the client, so it is fine to be hardcoded
  const key = 'client-VA8R8ZguTZTk6OcghxCA0swpDiABdrlmcrTRNiHKceg';
  let user;

  try {
    user = getStatsigUser({ req, res, router });
  } catch (e) {
    user = {
      locale: 'en',
      country: 'us',
      userAgent: 'build',
      appVersion: 'build',
      ip: 'build',
      custom: {
        brand: 'shutterstock',
        referer: 'build',
        url: 'build',
        pageUrl: router.asPath,
        isLoggedIn: false,
        pageLanguage: 'build',
        site: 'contributor',
        pageType: router.pathname,
        // subPage: undefined, // subPage not used in contributorWeb it seems like
        // 'test-e2e' value watched in custom segment used in experiments conditional override
        // to force experiments to control for automated testing
        // https://console.statsig.com/2FkSXPTvtQJC4nZkAA10eg/segments/segment%3Atest-e2e
        // ...(testE2e ? { 'test-e2e': testE2e } : {}),
      },
      customIDs: {
        stableID: res.locals?.anonymousId,
        anonymous_id: res.locals?.anonymousId,
        accounts_id: req.user?.id ? `${req.user.id}` : '',
      },
    };
  }

  if (res?.locals === undefined) {
    res.locals = {};
  }

  res.locals.statsigUser = user;

  const data = getStatsigData({ user, key });

  return {
    data,
    user,
    key,
  };
}

/**
 * Check a gate, should only be used in getServerSideProps as it relies on the statsigUser object
 * being set on res.locals in _app.getInitialProps
 * @param gate - The name of the gate to check
 * @param options - An object with a res property, used to get the statsigUser object
 * @returns The value of the gate
 */
export const checkGate = (gate: string, { res }: { res: Response }) => {
  const Statsig = getSwitchrInstance().getVendorClient();

  return Statsig.checkGate(res.locals.statsigUser, gate);
};

/**
 * Class which implements statsig's DynamicConfig interface w/out concern of bundle size increases
 */
export class FallbackDynamicConfig {
  public name: string;

  public value: Record<string, unknown>;

  constructor(name: string, value: Record<string, unknown>) {
    this.name = name;
    this.value = value;
  }

  // eslint-disable-next-line class-methods-use-this
  get<T>(_key: string, defaultValue: T) {
    return defaultValue;
  }

  // eslint-disable-next-line class-methods-use-this
  getIDType(): string | null {
    return null;
  }

  getValue() {
    return this.value;
  }

  getRuleID() {
    return this.value;
  }

  // eslint-disable-next-line class-methods-use-this
  getGroupName() {
    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  getEvaluationDetails() {
    return null;
  }

  // eslint-disable-next-line no-underscore-dangle, class-methods-use-this
  _getSecondaryExposures() {
    return [];
  }
}

/**
 * Check an experiment, should only be used in getServerSideProps as it relies on the statsigUser object
 * being set on res.locals in _app.getInitialProps
 * @param experimentId - The name of the gate to check
 * @param options - An object with a res property, used to get the statsigUser object
 * @param options.disableExposureLogging - A property to disable sending exposures for an experiment to analytics pipeline, only enable if paired w/ other impression recording in the front end
 * @returns Statsig DynamicConfig from experiment, or just empty object DynamicConfig if error.
 */
export const getExperiment = (
  experimentId: string,
  { res, disableExposureLogging = false }: { res: Response; disableExposureLogging?: boolean },
) => {
  try {
    return getSwitchrInstance().getVendorClient().getExperiment(res.locals.statsigUser, experimentId, {
      disableExposureLogging,
    });
  } catch (err) {
    console.warn(
      JSON.stringify({
        experimentId,
        message: err.message,
        stack: err.stack,
      }),
      'graceful degradation of Statsig: getExperiment',
    );

    return new FallbackDynamicConfig(experimentId, {});
  }
};
